/* eslint complexity: ["error", 7] -- hook determines when to show recommended group class modal */
import { useCallback, useEffect, useRef, useState } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { ModalPath, OnboardingScreenPath } from "@/constants/Navigation";
import { useOnboarding } from "@/context-providers/OnboardingProvider";
import { router } from "@/crossPlatform/expo-router";
import useRecommendedGroupClassModalVisibility from "@/hooks/recommendedGroupClassModal/useRecommendedGroupClassModalVisibility";
import { useModalTracker } from "@/hooks/useModalTracker";
import {
  UnencryptedStorageKey,
  useUnencryptedStorage,
} from "@/hooks/useUnencryptedStorage";
import { useGetBasicInfoQuery, useGetFullProfileQuery } from "@/store/api/user";
import { useRecommendedGroupClassModalDispatch } from "@/store/reducers/modals/recommendedGroupClassModal";
import { getIsOnboardedOnMobile } from "@/utils/user.util";

interface UseHomeEntryModalsResult {
  isRecommendedGroupCheckComplete: boolean;
}

/**
 * Handles Home entry modals and redirects: additional consent, legacy notifications
 * onboarding, and the recommended group class modal. This hook only runs effects (no
 * UI). When both the recommended group modal and SMT welcome could show, the
 * recommended group modal is launched first; the caller uses
 * isRecommendedGroupCheckComplete and activeModal to decide when to show SMT welcome.
 */
const useHomeEntryModals = (): UseHomeEntryModalsResult => {
  const { patientsNeedingConsent } = useOnboarding();
  const { activeModal } = useModalTracker();
  const { data: basicInfoData } = useGetBasicInfoQuery();
  const { isSuccess: isFullUserProfileSuccess } = useGetFullProfileQuery();
  const { getUnencryptedBooleanValue, saveUnencryptedBooleanValue } =
    useUnencryptedStorage();
  const {
    isLoading: isRecommendedGroupModalLoading,
    recommendations: recommendedGroupRecommendations,
    shouldShowRecommendedGroupModal,
  } = useRecommendedGroupClassModalVisibility();
  const { setQueue: setRecommendedGroupQueue } =
    useRecommendedGroupClassModalDispatch();

  const [isDisplayingOldOnboarding, setIsDisplayingOldOnboarding] =
    useState<boolean>(false);
  const hasLaunchedRecommendedGroupsModalRef = useRef(false);

  const handleAdditionalConsent = useCallback(async () => {
    if (
      activeModal === ModalPath.EditProfile ||
      isEmpty(patientsNeedingConsent)
    ) {
      return;
    }
    requestAnimationFrame(() => {
      router.navigate({
        params: {
          shouldShowPatientCards: "true",
        },
        pathname: OnboardingScreenPath.Consent,
      });
    });
  }, [activeModal, patientsNeedingConsent]);

  // Additional consent redirect when patients need consent (and not in old onboarding)
  useEffect(() => {
    if (
      isEmpty(patientsNeedingConsent) ||
      isDisplayingOldOnboarding ||
      !isFullUserProfileSuccess
    ) {
      return;
    }
    void handleAdditionalConsent();
  }, [
    isDisplayingOldOnboarding,
    activeModal,
    patientsNeedingConsent,
    handleAdditionalConsent,
    isFullUserProfileSuccess,
  ]);

  // Legacy notifications onboarding redirect (runs once, then marks dismissed)
  useEffect(() => {
    if (isNil(basicInfoData) || !isFullUserProfileSuccess) return;
    const { onboardings } = basicInfoData;
    const isUserOnboarded = getIsOnboardedOnMobile(onboardings);
    const isOnboardingDismissed = getUnencryptedBooleanValue(
      UnencryptedStorageKey.OnboardingNotificationsDismissed,
    );
    if (isOnboardingDismissed || isUserOnboarded) return;

    setIsDisplayingOldOnboarding(true);
    setTimeout(() => {
      router.navigate(OnboardingScreenPath.Notifications);
      saveUnencryptedBooleanValue({
        isTrue: true,
        key: UnencryptedStorageKey.OnboardingNotificationsDismissed,
      });
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicInfoData, isFullUserProfileSuccess]);

  // Recommended group class modal: seed queue and navigate (before SMT welcome when both apply)
  useEffect(() => {
    if (
      isRecommendedGroupModalLoading ||
      !shouldShowRecommendedGroupModal ||
      recommendedGroupRecommendations.length === 0 ||
      hasLaunchedRecommendedGroupsModalRef.current ||
      (activeModal !== null && activeModal !== ModalPath.RecommendedGroupClass)
      // NOTE: to prevent the modal from being shown, feel free to add a killswitch here
      // "remind me later" and "none of these times work" handling will be wired up in
      // https://teamequip.atlassian.net/browse/JN-2154
    ) {
      return;
    }
    hasLaunchedRecommendedGroupsModalRef.current = true;
    setRecommendedGroupQueue(recommendedGroupRecommendations);
    router.navigate(ModalPath.RecommendedGroupClass);
  }, [
    activeModal,
    isRecommendedGroupModalLoading,
    recommendedGroupRecommendations,
    setRecommendedGroupQueue,
    shouldShowRecommendedGroupModal,
  ]);

  return {
    isRecommendedGroupCheckComplete: !isRecommendedGroupModalLoading,
  };
};

export default useHomeEntryModals;
